import { calendarDisplayDate } from '../../../utils/date-time';
import { ReactNode } from 'react';
import moment from 'moment';
import { convertMinutesToHHMM } from '../../../components/utils';
import { Loading } from '../../../components/Loading';
import './Booking.css';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// Pecas do fluxo de agendamento em etapas, compartilhadas entre cliente e equipe.

export const formatDuration = (minutes: number) => {
    const total = Number(minutes) || 0;
    const hours = Math.floor(total / 60);
    const rest = total % 60;
    if (!hours) return `${rest} min`;
    return rest ? `${hours}h ${rest}min` : `${hours}h`;
};

export const capitalize = (value: string) => value.replace(/^./, (letter) => letter.toUpperCase());

type BookingHeaderProps = {
    steps: string[];
    step: string;
    title: string;
    recap?: string;
    canStepBack: boolean;
    onBack: () => void;
    loading?: boolean;
    // No modal de tela cheia, o voltar ja fica no cabecalho do modal.
    hideBackOnMobile?: boolean;
};

export const BookingHeader = ({ steps, step, title, recap, canStepBack, onBack, loading, hideBackOnMobile = true }: BookingHeaderProps) => {
    const current = Math.max(steps.indexOf(step), 0);

    return (
        <div className="px-1 pb-5">
            {canStepBack && (
                <button type="button" className={`sf-back-button mb-3 ${hideBackOnMobile ? 'hidden sm:inline-flex' : 'inline-flex'}`} disabled={loading} onClick={onBack}>
                    <ChevronLeft className="text-xs" aria-hidden="true" />
                    Voltar
                </button>
            )}
            <div className="flex gap-1" role="progressbar" aria-label="Progresso do agendamento" aria-valuemin={1} aria-valuemax={steps.length} aria-valuenow={current + 1}>
                {steps.map((item, index) => (
                    <span key={item} className={`h-1 flex-1 rounded-full transition-colors ${index <= current ? 'bg-primary' : 'bg-base-content/10'}`}></span>
                ))}
            </div>
            <p className="mt-3 text-xs font-medium text-base-content/55">Etapa {current + 1} de {steps.length}</p>
            <h2 className="mt-0.5 text-xl font-semibold tracking-tight text-base-content">{title}</h2>
            {!!recap && <p className="mt-1 truncate text-sm text-base-content/60">{recap}</p>}
        </div>
    );
};

type BookingBarProps = {
    title: string;
    detail?: string;
    action: string;
    disabled?: boolean;
    busy?: boolean;
    onClick: () => void;
    // `offset`: barra fixa na largura do conteudo; `inline`: dentro de modal ou painel.
    offset?: number;
    inline?: boolean;
};

export const BookingBar = ({ title, detail, action, disabled, busy, onClick, offset = 0, inline = false }: BookingBarProps) => (
    <div className={inline ? 'sf-booking-bar sf-booking-bar--inline' : 'sf-booking-bar fixed bottom-0 z-40'} style={inline ? undefined : { left: `${offset}px`, right: 0 }}>
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-4">
            <div className="min-w-0">
                <div className="truncate text-[0.9375rem] font-semibold tabular-nums text-base-content">{title}</div>
                {!!detail && <div className="truncate text-xs text-base-content/60">{detail}</div>}
            </div>
            <button type="button" className="btn btn-primary h-12 min-h-12 shrink-0 rounded-full px-6 text-[0.9375rem]" disabled={!!disabled} onClick={onClick}>
                {busy ? <Loading /> : action}
            </button>
        </div>
    </div>
);

type WeekStripProps = {
    timezone?: string;
    date: moment.Moment;
    onSelect: (day: moment.Moment) => void;
    onPrevious: () => void;
    onNext: () => void;
    canPrevious?: boolean;
    canNext?: boolean;
    isDayDisabled?: (day: moment.Moment) => boolean;
    disabled?: boolean;
};

export const WeekStrip = ({ date, onSelect, onPrevious, onNext, canPrevious = true, canNext = true, isDayDisabled, disabled = false, timezone }: WeekStripProps) => {
    const today = moment(calendarDisplayDate(new Date(), timezone)).startOf('day');
    const start = moment(date).startOf('week');
    const days = Array.from({ length: 7 }, (_, index) => start.clone().add(index, 'days'));
    const arrow = 'flex h-10 w-10 items-center justify-center rounded-full text-sm text-primary disabled:text-base-content/20';

    return (
        <div className="rounded-2xl bg-base-100 p-3 ring-1 ring-base-content/[0.06]">
            <div className="flex items-center justify-between pb-2 pl-2">
                <span className="text-sm font-semibold text-base-content">{capitalize(moment(date).format('MMMM [de] YYYY'))}</span>
                <div className="flex">
                    <button type="button" aria-label="Semana anterior" disabled={disabled || !canPrevious} className={arrow} onClick={onPrevious}>
                        <ChevronLeft aria-hidden="true" />
                    </button>
                    <button type="button" aria-label="Próxima semana" disabled={disabled || !canNext} className={arrow} onClick={onNext}>
                        <ChevronRight aria-hidden="true" />
                    </button>
                </div>
            </div>
            <div className="grid grid-cols-7 gap-0.5">
                {days.map((day) => {
                    const selected = day.isSame(date, 'day');
                    const dayDisabled = disabled || !!isDayDisabled?.(day);
                    const isToday = day.isSame(today, 'day');
                    return (
                        <button
                            type="button"
                            key={day.format('YYYY-MM-DD')}
                            disabled={dayDisabled}
                            aria-pressed={selected}
                            aria-label={`${day.format('dddd, DD [de] MMMM')}${isToday ? ', hoje' : ''}`}
                            onClick={() => onSelect(day)}
                            className={`flex min-h-[3.5rem] flex-col items-center justify-center rounded-xl transition-colors ${
                                selected ? 'bg-primary text-primary-content' : dayDisabled ? 'text-base-content/25' : 'text-base-content hover:bg-base-content/5'
                            }`}
                        >
                            <span className={`text-[0.625rem] font-medium uppercase ${selected ? '' : 'opacity-60'}`}>{day.format('ddd').replace('.', '')}</span>
                            <span className={`text-base font-semibold tabular-nums ${isToday && !selected && !dayDisabled ? 'text-primary' : ''}`}>{day.format('DD')}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

const PERIODS = [
    { label: 'Manhã', from: 0, to: 720 },
    { label: 'Tarde', from: 720, to: 1080 },
    { label: 'Noite', from: 1080, to: 1440 },
];

export const SlotGrid = ({ slots, selected, onSelect, disabled }: { slots: number[]; selected: number; onSelect: (slot: number) => void; disabled?: boolean }) => (
    <div className="space-y-4">
        {PERIODS.map((period) => {
            const items = slots.filter((slot) => slot >= period.from && slot < period.to);
            if (!items.length) return null;
            return (
                <section key={period.label}>
                    <h3 className="mb-2 px-1 text-sm font-medium text-base-content/60">{period.label}</h3>
                    <div className="grid grid-cols-4 gap-2 sm:grid-cols-5">
                        {items.map((slot) => (
                            <button
                                key={slot}
                                type="button"
                                disabled={disabled}
                                aria-pressed={slot === selected}
                                onClick={() => onSelect(slot)}
                                className={`h-11 rounded-xl text-sm font-semibold tabular-nums transition-colors ${
                                    slot === selected ? 'bg-primary text-primary-content' : 'bg-base-100 text-base-content ring-1 ring-base-content/[0.06] hover:bg-base-100/70'
                                }`}
                            >
                                {convertMinutesToHHMM(slot)}
                            </button>
                        ))}
                    </div>
                </section>
            );
        })}
    </div>
);

export const SlotSkeleton = ({ label }: { label: string }) => (
    <div className="grid grid-cols-4 gap-2" role="status" aria-label={label}>
        {Array.from({ length: 8 }).map((_, index) => <div key={index} className="sf-skeleton h-11 rounded-xl" />)}
    </div>
);

export const LoadFailure = ({ message, onRetry }: { message: string; onRetry: () => void }) => (
    <div className="py-8 text-center" role="alert">
        <p className="text-sm text-base-content/70">{message}</p>
        <button type="button" className="btn btn-ghost btn-sm mt-2 text-primary" onClick={onRetry}>Tentar novamente</button>
    </div>
);

export const SummaryRow = ({ label, children, onEdit, editLabel }: { label: string; children: ReactNode; onEdit?: () => void; editLabel?: string }) => (
    <div className="flex items-start gap-3 border-b border-base-content/[0.08] px-4 py-3.5 last:border-b-0">
        <div className="min-w-0 flex-1">
            <div className="text-xs text-base-content/55">{label}</div>
            <div className="mt-0.5 break-words text-[0.9375rem] font-medium text-base-content">{children}</div>
        </div>
        {onEdit && (
            <button type="button" className="shrink-0 pt-3 text-sm font-medium text-primary" aria-label={editLabel} onClick={onEdit}>
                Alterar
            </button>
        )}
    </div>
);

export const initials = (name?: string) =>
    String(name || '?').trim().split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase();
